import {
  ItineraryRequest,
  ItinerarySearchItineraryRequest,
  SearchItineraryRequest,
  SearchRequest,
  SegmentRequest,
  UnbundledMappingGroupingType,
} from 'generated/graphql';
import { OF1SearchRequest, PerformedSearchRequestContext } from '.';

export const buildSearchQuerySegment = ({
  date,
  departure,
  destination,
}): SegmentRequest => ({
  date,
  departure: {
    iata: departure.iata,
    geoNodeId: departure.geoNodeId,
  },
  destination: {
    iata: destination.iata,
    geoNodeId: destination.geoNodeId,
  },
});

// Old search itinerary builders
export const buildSearchQueryItinerary = (args): ItineraryRequest => {
  const { resident, mainAirportsOnly } = args;
  return {
    ...buildSearchItineraryItineraryQuery(args),
    resident,
    mainAirportsOnly,
  };
};

export const buildSearchQuery = ({
  buyPath,
  tripType,
  itinerary,
}): SearchRequest => ({
  buyPath,
  tripType,
  itinerary: buildSearchQueryItinerary(itinerary),
});

// New searchItinerary request builders
export const buildSearchItineraryItineraryQuery = ({
  numAdults,
  numChildren,
  numInfants,
  cabinClass,
  externalSelection,
  segments,
  excludeCarriers,
}): ItinerarySearchItineraryRequest => ({
  numAdults,
  numChildren,
  numInfants,
  cabinClass,
  externalSelection,
  segments: segments.map(buildSearchQuerySegment),
  excludeCarriers: excludeCarriers || false,
});

export const buildSearchItineraryQuery = ({
  buyPath,
  tripType,
  unbundledMappingGrouping = UnbundledMappingGroupingType.Default,
  itinerary,
}): SearchItineraryRequest => ({
  buyPath,
  tripType,
  unbundledMappingGrouping,
  itinerary: buildSearchItineraryItineraryQuery(itinerary),
});

export const buyPathMap = {
  UNKNOWN: 70,
  FLIGHTS_HOME_SEARCH_FORM: 71,
  FLIGHTS_HOME_RECENT_SEARCHES: 72,
  FLIGHTS_RESULTS_SEARCH_FORM: 73,
  FLIGHTS_DIRECT_URL: 74,
  FLIGHTS_EXPIRED_SEARCH_POPUP: 75,
  FLIGHTS_EXPIRED_SESSION_POPUP: 76,
  CONFIRMATION_REJECTED: 77,
  BOOKING_ERROR: 78,
  WEEKEND_DEALS: 80,
  CALENDAR_SEARCH_FLEXIBLE_DATES: 81,
  INSPIRATIONAL_SEARCH: 82,
  HOTTEST_DEALS: 83,
  PASSENGER_PAGE_SEARCH: 84,
  INSPIRATIONAL_DEALS_PAGE: 86,
  EDIT_INSPIRATIONAL_SEARCH: 87,
  SEM_RESULTS: 98,
  FLIGHTS_HOME_SEARCH_FORM_MICROSITE: 99,
  FLIGHTS_HOME_FLIGHTS_TAB: 5200,
  FLIGHTS_HOME_DYNPACK_TAB: 5201,
  DYNPACK_MOBILE_HOME: 5205,
  DYNPACK_MOBILE_FLIGHTS_TAB: 5208,
  DYNPACK_MOBILE_RESULTS_CHANGE_SEARCH: 5209,
  DYNPACK_MOBILE_ACCOMMODATION_CHANGE_SEARCH: 5210,
  DYNPACK_MOBILE_BOOKING_CHANGE_SEARCH: 5211,
  DYNPACK_HOME_FLIGHTS_TAB: 5202,
  DYNPACK_HOME_DYNPACK_TAB: 5203,
  DYNPACK_RESULTS_SEARCH_FORM: 5204,
  DYNPACK_RESULTS_ACCOMMODATION_SEARCH_FORM: 5212,
  DYNPACK_RESULTS_FLIGHTS_SEARCH_FORM: 5213,
  DYNPACK_ROOM_SELECTION_SEARCH_FORM: 5207,
  DYNPACK_DIRECT_URL: 5220,
  DYNPACK_EXPIRED_ROOMSELECTION_POPUP: 5231,
  DYNPACK_EXPIRED_SEARCH_POPUP: 5230,
  RESERVED_4: 79,
  UPSELL_DYNPACK_EMERGING_LAYER_REDIRECTION: 5004,
  INTERMEDIATE_PAGE_FLIGHTS_SEARCH: 5007,
  INTERMEDIATE_PAGE_DYNPACK_SEARCH: 5008,
  DYNPACK_HOME_DYNPACK_TAB_MICROSITE: 5513,
  POST_BOOKING_ACCOMMODATION: 5050,
  KO_EMAIL_SAME_SEARCH: 154,
  TRANSACTIONAL_EMAILS: 155,
  BIM_NOTIFICATIONS_REBOOK_F: 6000,
  BIM_NOTIFICATIONS_REBOOK_DP: 6001,
  SSLP_AIRLINES_INDEX: 1000,
  SSLP_AIRLINE_CITY_PAIRS: 1001,
  SSLP_AIRLINES: 1002,
  SSLP_CITY_PAIRS: 1003,
  SSLP_COUNTRIES: 1004,
  SSLP_DYNPACK_DESTINATIONS: 1005,
  SSLP_FLIGHT_DESTINATIONS: 1006,
  SSLP_GENERIC: 1007,
  SSLP_INTERNATIONAL: 1008,
  SSLP_HOTELS: 1009,
  SSLP_CITY: 1010,
  SSLP_CARS_INTERNATIONAL: 1011,
  SSLP_CARS_COUNTRIES: 1012,
  SSLP_CARS_DESTINATIONS: 1013,
  SSLP_CARS_HOME: 1014,
  SSLP_SEARCHER: 1015,
  SSLP_CITY_COUNTRY: 1016,
  SSLP_CITY_STATE: 1017,
  SSLP_TRAINS_DESTINATION: 1018,
  SSLP_TRAINS_CITY_PAIRS: 1019,
  SSLP_TRAINS: 1020,
  SSLP_TRAIN_CARRIER: 7278,
  SSLP_SEARCHEXTENSION: 7276,
  SSLP_HOTELS_PROPERTIES: 7254,
  HSA_FLIGHT_CONFIRMATION: 7255,
  HSA_RESULTS_SEARCH_FORM: 7204,
  HSA_PB_SEARCHEXTENSION: 7225,
  HOME_HSA_TAB: 7201,
  HSA_PB_POPUNDER: 7224,
  HSA_HOME: 7202,
  HSA_ROOM_SELECTION_SEARCH_FORM: 7207,
  HSA_DIRECT_URL: 7220,
  HSA_HOME_DEALS: 7237,
  HSA_PB_YOURJOURNEY: 7230,
  HSA_CRM: 7238,
  HSA_LANDING_WMP: 7239,
  HSA_MAP: 7242,
  HSA_NATIVE_SEARCH_FORM: 7240,
  HSA_PB_EMERGINGLAYER: 7221,
  HSA_PB_GIFT: 7231,
  HSA_PB_MOBILE_DEALS: 7259,
  HSA_ROOM_SHARE: 7241,
};

const isNumeric = (buyPath) => /\d+/.test(buyPath);

export const parseBuyPath = (buyPath) => {
  if (isNumeric(buyPath)) {
    return parseInt(buyPath, 10);
  }
  return buyPathMap[buyPath] || buyPathMap.UNKNOWN;
};

const tripTypesOf1ToRequestMap: Record<
  string,
  PerformedSearchRequestContext['tripType']
> = {
  oneWayTrip: 'ONE_WAY',
  roundTrip: 'ROUND_TRIP',
  multipleTrip: 'MULTIPLE_DESTINATIONS',
};

const parseExternalSelection = (externalSelection) =>
  externalSelection?.searchId ? externalSelection : null;

const parseLocationRequest = (location) => ({
  ...location,
  type:
    location.type === 'IATA_CODE' && location.geoNodeType
      ? location.geoNodeType
      : location.type,
  cityName: location.city,
  cityIata: location.iata,
});

const parseSegmentRequest = (segment) => ({
  date: segment.dateStr,
  departure: parseLocationRequest(segment.departure),
  destination: parseLocationRequest(segment.destination),
});

export const parseClientSearchParams: (
  params: OF1SearchRequest,
  bookingError?: boolean
) => PerformedSearchRequestContext = (
  {
    buyPath,
    cabinClass = 'TOURIST',
    resident = false,
    mainAirportsOnly = false,
    trainSearch = false,
    unbundledMappingGrouping = trainSearch
      ? UnbundledMappingGroupingType.TransportTypes
      : UnbundledMappingGroupingType.Default,
    numAdults = 1,
    numChildren = 0,
    numInfants = 0,
    type,
    externalSelectionRequest,
    directFlightsOnly: withoutConnections,
    airlinesCodes: airlineCodes,
    searchSegments,
    ...rest
  },
  bookingError = false
) => ({
  buyPath: parseBuyPath(buyPath),
  tripType: tripTypesOf1ToRequestMap[type],
  withoutConnections,
  trainSearch,
  unbundledMappingGrouping,
  airlineCodes,
  itinerary: {
    numAdults,
    numChildren,
    numInfants,
    cabinClass,
    resident,
    mainAirportsOnly,
    externalSelection: parseExternalSelection(externalSelectionRequest),
    segments: searchSegments.map(parseSegmentRequest),
    excludeCarriers: bookingError,
  },
  ...rest,
});
